import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import { useParams, useSearchParams } from "react-router-dom";
import { ordersApi } from "../../api/orders";
import { projectsApi } from "../../api/projects";
import { useProjects } from "../../context/ProjectsContext";
import { isProjectModuleEnabled } from "../../utils/projectModules";
import { isProjectCapabilityEnabled } from "../../utils/projectCapabilities";
import { hasProjectRole, normalizeProjectAccess } from "../../utils/projectAccess";
import ModuleDisabledState from "../../components/dashboard/ModuleDisabledState";

import EditorialIntro from "../../components/dashboard/EditorialIntro";
const BRAND = { orange: "#F35A3A", dark: "#19242F" };
const PAGE_SIZE = 20;

const STATUS_META = {
  received: { label: "Recibido", color: "info" },
  awaiting_payment: { label: "Esperando pago", color: "warning" },
  payment_pending: { label: "Pago pendiente", color: "warning" },
  paid: { label: "Pagado", color: "success" },
  payment_failed: { label: "Pago rechazado", color: "error" },
  requires_review: { label: "Revisar", color: "error" },
  processing: { label: "Preparando", color: "info" },
  ready: { label: "Listo", color: "success" },
  shipped: { label: "Enviado", color: "info" },
  delivered: { label: "Entregado", color: "success" },
  cancelled: { label: "Cancelado", color: "default" },
  refunded: { label: "Reembolsado", color: "default" },
};

const PAYMENT_META = {
  not_started: { label: "Sin iniciar", color: "default" },
  pending: { label: "Pendiente", color: "warning" },
  in_process: { label: "Procesando", color: "warning" },
  authorized: { label: "Autorizado", color: "info" },
  approved: { label: "Aprobado", color: "success" },
  rejected: { label: "Rechazado", color: "error" },
  cancelled: { label: "Cancelado", color: "default" },
  refunded: { label: "Reembolsado", color: "default" },
  charged_back: { label: "Contracargo", color: "error" },
  unknown: { label: "Desconocido", color: "default" },
};

function formatMoney(value, currency = "ARS") {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: currency || "ARS",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

function statusChip(status) {
  const meta = STATUS_META[status] || { label: status || "Sin estado", color: "default" };
  return <Chip size="small" label={meta.label} color={meta.color} sx={{ fontWeight: 850 }} />;
}

function paymentChip(status) {
  const meta = PAYMENT_META[status] || { label: status || "Sin estado", color: "default" };
  return <Chip size="small" label={meta.label} color={meta.color} variant="outlined" sx={{ fontWeight: 850 }} />;
}

function isCoordinatedOrder(order) {
  if (!order) return false;
  if (order.paymentMode) return order.paymentMode === "coordinate";
  return order.payment?.provider === "coordinate";
}

function orderPaymentChip(order) {
  if (isCoordinatedOrder(order)) {
    return (
      <Chip
        size="small"
        label="Pago a coordinar"
        color="info"
        variant="outlined"
        sx={{ fontWeight: 850 }}
      />
    );
  }
  return paymentChip(order?.paymentStatus);
}

function possibleStatuses(order) {
  if (!order || ["cancelled", "refunded", "delivered"].includes(order.status)) {
    return [];
  }

  if (isCoordinatedOrder(order) || order.paymentStatus === "approved") {
    return [
      { value: "processing", label: "Preparando" },
      { value: "ready", label: "Listo para retirar" },
      { value: "shipped", label: "Enviado" },
      { value: "delivered", label: "Entregado" },
      { value: "cancelled", label: "Cancelar pedido" },
    ];
  }

  return [{ value: "cancelled", label: "Cancelar pedido" }];
}

const KITCHEN_STATE_META = {
  open: { label: "Abierta", color: "warning" },
  closed: { label: "Cerrada", color: "success" },
};

const KITCHEN_MODE_META = {
  table: "En mesa",
  pickup: "Retiro",
  delivery: "Entrega",
  other: "Otro",
};

function kitchenStateChip(order) {
  const state = order?.orderContext?.commandState || "open";
  const meta = KITCHEN_STATE_META[state] || { label: state || "Sin estado", color: "default" };
  return <Chip size="small" label={meta.label} color={meta.color} sx={{ fontWeight: 850 }} />;
}

function kitchenModeLabel(order) {
  return KITCHEN_MODE_META[order?.orderContext?.serviceMode] || "Otro";
}

function kitchenTableLabel(order) {
  const table = order?.orderContext?.tableSnapshot;
  if (!table) return "Sin mesa";
  const identifier = table.identifier ? `Mesa ${table.identifier}` : "Mesa";
  return `${identifier}${table.name ? ` · ${table.name}` : ""}`;
}

export default function ProjectOrdersPage() {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const { projects, activeProject, setActiveProject, loadProjects } = useProjects();
  const [project, setProject] = React.useState(null);
  const [memberAccess, setMemberAccess] = React.useState(normalizeProjectAccess());
  const [orders, setOrders] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [query, setQuery] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [selectedOrder, setSelectedOrder] = React.useState(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [updating, setUpdating] = React.useState(false);
  const [nextStatus, setNextStatus] = React.useState("");

  const contextProject = React.useMemo(
    () => projects.find((item) => item.slug === projectId || item.id === projectId) || activeProject,
    [projects, projectId, activeProject]
  );
  const resolvedProject = project || contextProject;
  const ordersEnabled = isProjectModuleEnabled(resolvedProject, "orders");
  const ecommerceEnabled = isProjectModuleEnabled(resolvedProject, "ecommerce");
  const kitchenEnabled = isProjectCapabilityEnabled(resolvedProject, "kitchenOrders");
  const kitchenRequested = String(searchParams.get("vista") || "").trim().toLowerCase() === "comandas";
  const kitchenView = kitchenRequested && kitchenEnabled;
  const canManageKitchen = hasProjectRole(memberAccess, [
    "owner",
    "admin",
    "administrativo",
    "vendedor",
    "cajero",
  ]);

  React.useEffect(() => {
    if (!projects.length) loadProjects();
  }, [projects.length, loadProjects]);

  React.useEffect(() => {
    let active = true;
    projectsApi.get(projectId)
      .then((data) => {
        if (!active) return;
        setProject(data.project || null);
        setMemberAccess(normalizeProjectAccess(data.memberAccess || data.project?.memberAccess || {}));
        if (data.project) setActiveProject(data.project);
      })
      .catch(() => {
        if (active) setMemberAccess(normalizeProjectAccess());
      });
    return () => {
      active = false;
    };
  }, [projectId, setActiveProject]);

  const loadOrders = React.useCallback(async () => {
    if (!ordersEnabled || (kitchenRequested && !kitchenEnabled)) {
      setLoading(false);
      setOrders([]);
      setTotal(0);
      return;
    }
    try {
      setLoading(true);
      setError("");
      const data = kitchenView
        ? await ordersApi.listKitchen(projectId, {
            page,
            pageSize: PAGE_SIZE,
            q: query.trim(),
            state: status,
          })
        : await ordersApi.list(projectId, {
            page,
            pageSize: PAGE_SIZE,
            q: query.trim(),
            status,
            excludeKitchen: 1,
          });
      setOrders(data.orders || []);
      setTotal(Number(data.total || 0));
    } catch (err) {
      setOrders([]);
      setTotal(0);
      setError(err.message || (kitchenView ? "No se pudieron cargar las comandas" : "No se pudieron cargar los pedidos"));
    } finally {
      setLoading(false);
    }
  }, [ordersEnabled, kitchenRequested, kitchenEnabled, kitchenView, projectId, page, query, status]);

  React.useEffect(() => {
    const timer = window.setTimeout(loadOrders, 250);
    return () => window.clearTimeout(timer);
  }, [loadOrders]);

  React.useEffect(() => {
    setPage(1);
    setStatus("");
    setSelectedOrder(null);
    setNextStatus("");
  }, [kitchenView, kitchenRequested]);

  const openOrder = async (order) => {
    setSelectedOrder(order);
    setNextStatus("");
    if (kitchenView) {
      setDetailLoading(false);
      return;
    }
    try {
      setDetailLoading(true);
      const data = await ordersApi.get(projectId, order.id || order.orderId || order._id);
      setSelectedOrder(data.order || order);
    } catch (err) {
      setError(err.message || "No se pudo abrir el pedido");
    } finally {
      setDetailLoading(false);
    }
  };

  const updateStatus = async () => {
    if (!selectedOrder || !nextStatus) return;
    try {
      setUpdating(true);
      setError("");
      const orderId = selectedOrder.id || selectedOrder.orderId || selectedOrder._id;
      const data = await ordersApi.updateStatus(projectId, orderId, nextStatus);
      setSelectedOrder(data.order || selectedOrder);
      setNextStatus("");
      await loadOrders();
    } catch (err) {
      setError(err.message || "No se pudo actualizar el pedido");
    } finally {
      setUpdating(false);
    }
  };

  const closeKitchenOrder = async () => {
    if (!selectedOrder || !canManageKitchen) return;
    try {
      setUpdating(true);
      setError("");
      const orderId = selectedOrder.id || selectedOrder.orderId || selectedOrder._id;
      const data = await ordersApi.closeKitchen(projectId, orderId);
      setSelectedOrder(data.order || selectedOrder);
      await loadOrders();
    } catch (err) {
      setError(err.message || "No se pudo cerrar la comanda");
    } finally {
      setUpdating(false);
    }
  };

  if ((!ordersEnabled || (kitchenRequested && !kitchenEnabled)) && !loading) {
    return (
      <ModuleDisabledState
        projectId={projectId}
        title={kitchenRequested ? "Comandas" : "Pedidos"}
        description={kitchenRequested
          ? "Activá Comandas junto con Pedidos para usar el circuito gastronómico existente."
          : "Este nivel permite registrar compradores, coordinar retiro o entrega y seguir el estado de cada pedido sin exigir Mercado Pago."}
      />
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const paidCount = orders.filter((order) => order.paymentStatus === "approved").length;
  const pendingCount = orders.filter((order) => ["received", "awaiting_payment", "payment_pending", "requires_review"].includes(order.status)).length;
  const openKitchenCount = orders.filter((order) => order.orderContext?.commandState !== "closed").length;
  const tableKitchenCount = orders.filter((order) => order.orderContext?.serviceMode === "table").length;

  return (
    <Stack spacing={3}>
      <EditorialIntro
        title={kitchenView ? "Comandas" : "Pedidos"}
        titleVariant="h3"
        context={`${project?.name || contextProject?.name || projectId} · ${kitchenView ? "operación de cocina y mesa" : "retiro, entrega y seguimiento"}.`}
        helpTitle={kitchenView ? "Cómo se relaciona con Pedidos" : "Qué reúne un pedido"}
        helpText={kitchenView
          ? "Comandas usa el mismo pedido como fuente operativa y muestra sólo lo necesario para preparar, servir y cerrar la atención gastronómica."
          : "Cada pedido conserva cliente, artículos, entrega, pago y estado operativo para seguirlo sin separar la información en pantallas técnicas."}
        action={<Button startIcon={<RefreshRoundedIcon />} onClick={loadOrders} variant="outlined" sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}>Actualizar</Button>}
      />

      {kitchenView ? (
        <Alert severity="info">
          <strong>Comandas</strong> usa los pedidos existentes con contexto gastronómico. Acá se muestran separadas de los pedidos web para no duplicar la misma operación.
        </Alert>
      ) : !ecommerceEnabled ? (
        <Alert severity="success">
          <strong>Pedidos</strong> funciona sin Mercado Pago: los pedidos ingresan para coordinar pago, retiro o entrega con el comprador.
        </Alert>
      ) : null}
      {error && <Alert severity="error">{error}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
            <CardContent>
              <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.62)", fontWeight: 800 }}>TOTAL</Typography>
              <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.dark, mt: 0.5 }}>{total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
            <CardContent>
              <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.62)", fontWeight: 800 }}>
                {kitchenView ? "ABIERTAS EN ESTA PÁGINA" : "PAGADOS EN ESTA PÁGINA"}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.dark, mt: 0.5 }}>
                {kitchenView ? openKitchenCount : paidCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
            <CardContent>
              <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.62)", fontWeight: 800 }}>
                {kitchenView ? "EN MESA EN ESTA PÁGINA" : "PENDIENTES EN ESTA PÁGINA"}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.dark, mt: 0.5 }}>
                {kitchenView ? tableKitchenCount : pendingCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 2.5 }}>
            <TextField
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(1);
              }}
              label={kitchenView ? "Buscar comanda, cliente u origen" : "Buscar pedido, cliente o email"}
              fullWidth
              InputProps={{ startAdornment: <SearchRoundedIcon sx={{ mr: 1, color: "rgba(25,36,47,0.45)" }} /> }}
            />
            <TextField
              select
              label={kitchenView ? "Estado de comanda" : "Estado"}
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(1);
              }}
              sx={{ minWidth: { xs: "100%", md: 220 } }}
            >
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(kitchenView ? KITCHEN_STATE_META : STATUS_META).map(([value, meta]) => (
                <MenuItem key={value} value={value}>{meta.label}</MenuItem>
              ))}
            </TextField>
          </Stack>

          {loading ? (
            <Stack alignItems="center" sx={{ py: 7 }} spacing={2}>
              <CircularProgress />
              <Typography>{kitchenView ? "Cargando comandas..." : "Cargando pedidos..."}</Typography>
            </Stack>
          ) : orders.length === 0 ? (
            <Alert severity="info">Todavía no hay {kitchenView ? "comandas" : "pedidos"} con estos filtros.</Alert>
          ) : (
            <>
              <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid rgba(25,36,47,0.07)", borderRadius: 3 }}>
                <Table>
                  <TableHead>
                    {kitchenView ? (
                      <TableRow sx={{ bgcolor: "#F6F7FB" }}>
                        <TableCell sx={{ fontWeight: 900 }}>Comanda</TableCell>
                        <TableCell sx={{ fontWeight: 900 }}>Modalidad</TableCell>
                        <TableCell sx={{ fontWeight: 900 }}>Mesa / cliente</TableCell>
                        <TableCell sx={{ fontWeight: 900 }}>Estado</TableCell>
                        <TableCell sx={{ fontWeight: 900 }}>Ítems</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 900 }}>Total</TableCell>
                      </TableRow>
                    ) : (
                      <TableRow sx={{ bgcolor: "#F6F7FB" }}>
                        <TableCell sx={{ fontWeight: 900 }}>Pedido</TableCell>
                        <TableCell sx={{ fontWeight: 900 }}>Cliente</TableCell>
                        <TableCell sx={{ fontWeight: 900 }}>Entrega</TableCell>
                        <TableCell sx={{ fontWeight: 900 }}>Estado</TableCell>
                        <TableCell sx={{ fontWeight: 900 }}>Pago</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 900 }}>Total</TableCell>
                      </TableRow>
                    )}
                  </TableHead>
                  <TableBody>
                    {orders.map((order) => (
                      <TableRow
                        key={order.id || order.orderId || order._id}
                        hover
                        onClick={() => openOrder(order)}
                        sx={{ cursor: "pointer" }}
                      >
                        {kitchenView ? (
                          <>
                            <TableCell>
                              <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>{order.orderNumber || "Comanda"}</Typography>
                              <Typography variant="caption" sx={{ color: "rgba(25,36,47,0.56)" }}>{formatDate(order.createdAt)}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontWeight: 800 }}>{kitchenModeLabel(order)}</Typography>
                              <Typography variant="caption" sx={{ color: "rgba(25,36,47,0.56)" }}>{order.source || ""}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontWeight: 800 }}>
                                {order.orderContext?.serviceMode === "table"
                                  ? kitchenTableLabel(order)
                                  : order.customer?.fullName || "Sin cliente"}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "rgba(25,36,47,0.56)" }}>
                                {order.customer?.fullName && order.orderContext?.serviceMode === "table"
                                  ? order.customer.fullName
                                  : order.customer?.email || ""}
                              </Typography>
                            </TableCell>
                            <TableCell>{kitchenStateChip(order)}</TableCell>
                            <TableCell>{(order.items || []).reduce((sum, item) => sum + Number(item.quantity || 0), 0)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 900 }}>{formatMoney(order.total, order.currency)}</TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell>
                              <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>{order.orderNumber || "Pedido"}</Typography>
                              <Typography variant="caption" sx={{ color: "rgba(25,36,47,0.56)" }}>{formatDate(order.createdAt)}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontWeight: 800 }}>{order.customer?.fullName || "—"}</Typography>
                              <Typography variant="caption" sx={{ color: "rgba(25,36,47,0.56)" }}>{order.customer?.email || ""}</Typography>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                {order.fulfillment?.method === "delivery" ? <LocalShippingRoundedIcon fontSize="small" /> : <StorefrontRoundedIcon fontSize="small" />}
                                <Typography variant="body2">{order.fulfillment?.method === "delivery" ? "Envío" : "Retiro"}</Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>{statusChip(order.status)}</TableCell>
                            <TableCell>{orderPaymentChip(order)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 900 }}>{formatMoney(order.total, order.currency)}</TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalPages > 1 && (
                <Stack alignItems="center" sx={{ mt: 2.5 }}>
                  <Pagination page={page} count={totalPages} onChange={(_event, value) => setPage(value)} />
                </Stack>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(selectedOrder)} onClose={() => setSelectedOrder(null)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 950, color: BRAND.dark }}>
          {selectedOrder?.orderNumber || (kitchenView ? "Detalle de la comanda" : "Detalle del pedido")}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Stack alignItems="center" sx={{ py: 5 }}><CircularProgress /></Stack>
          ) : selectedOrder ? (
            <Stack spacing={3}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="overline" sx={{ fontWeight: 900 }}>CLIENTE</Typography>
                  <Typography sx={{ fontWeight: 900 }}>{selectedOrder.customer?.fullName}</Typography>
                  <Typography>{selectedOrder.customer?.email}</Typography>
                  <Typography>{selectedOrder.customer?.numberphone}</Typography>
                  {selectedOrder.customer?.dni && <Typography>DNI: {selectedOrder.customer.dni}</Typography>}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="overline" sx={{ fontWeight: 900 }}>
                    {kitchenView ? "OPERACIÓN" : "ENTREGA"}
                  </Typography>
                  {kitchenView ? (
                    <>
                      <Typography sx={{ fontWeight: 900 }}>{kitchenModeLabel(selectedOrder)}</Typography>
                      {selectedOrder.orderContext?.serviceMode === "table" ? (
                        <Typography>{kitchenTableLabel(selectedOrder)}</Typography>
                      ) : null}
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        {kitchenStateChip(selectedOrder)}
                      </Stack>
                    </>
                  ) : (
                    <>
                      <Typography sx={{ fontWeight: 900 }}>
                        {selectedOrder.fulfillment?.method === "delivery" ? "Envío a domicilio" : "Retiro en el local"}
                      </Typography>
                      {selectedOrder.fulfillment?.address && (
                        <Typography>
                          {selectedOrder.fulfillment.address.street} {selectedOrder.fulfillment.address.streetNumber}, {selectedOrder.fulfillment.address.city}
                        </Typography>
                      )}
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        {statusChip(selectedOrder.status)}
                        {orderPaymentChip(selectedOrder)}
                      </Stack>
                    </>
                  )}
                </Grid>
              </Grid>

              <Divider />

              <Box>
                <Typography variant="h6" sx={{ fontWeight: 950, mb: 1.5 }}>Productos</Typography>
                <Stack spacing={1.25}>
                  {(selectedOrder.items || []).map((item, index) => (
                    <Stack key={`${item.productId || index}-${index}`} direction="row" justifyContent="space-between" spacing={2} sx={{ p: 1.5, borderRadius: 2.5, bgcolor: "#F6F7FB" }}>
                      <Box>
                        <Typography sx={{ fontWeight: 900 }}>{item.name}</Typography>
                        {item.variantLabel ? (
                          <Typography variant="body2" sx={{ color: BRAND.orange, fontWeight: 850 }}>
                            {item.variantLabel}{item.variantSku ? ` · SKU ${item.variantSku}` : ""}
                          </Typography>
                        ) : null}
                        <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.62)" }}>
                          {item.quantity} × {formatMoney(item.unitPrice, selectedOrder.currency)}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontWeight: 900 }}>{formatMoney(item.lineTotal, selectedOrder.currency)}</Typography>
                    </Stack>
                  ))}
                </Stack>
                <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>Total</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 950, color: BRAND.orange }}>{formatMoney(selectedOrder.total, selectedOrder.currency)}</Typography>
                </Stack>
              </Box>

              {selectedOrder.notes && <Alert severity="info">{selectedOrder.notes}</Alert>}

              {!kitchenView && possibleStatuses(selectedOrder).length > 0 && (
                <Card elevation={0} sx={{ bgcolor: "#F6F7FB", borderRadius: 3 }}>
                  <CardContent>
                    <Typography sx={{ fontWeight: 950, mb: 1.5 }}>Actualizar estado</Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                      <TextField select value={nextStatus} onChange={(event) => setNextStatus(event.target.value)} label="Nuevo estado" fullWidth>
                        {possibleStatuses(selectedOrder).map((option) => (
                          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </TextField>
                      <Button
                        variant="contained"
                        disabled={!nextStatus || updating}
                        onClick={updateStatus}
                        sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900, minWidth: 150 }}
                      >
                        {updating ? "Guardando..." : "Actualizar"}
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              )}
              {kitchenView && selectedOrder.orderContext?.commandState !== "closed" ? (
                <Card elevation={0} sx={{ bgcolor: "#F6F7FB", borderRadius: 3 }}>
                  <CardContent>
                    <Typography sx={{ fontWeight: 950 }}>Comanda abierta</Typography>
                    <Typography variant="body2" sx={{ color: "rgba(25,36,47,.62)", mt: .5 }}>
                      {canManageKitchen
                        ? "Cerrala cuando la atención gastronómica haya terminado. Los ítems y el historial permanecen en el pedido."
                        : "Podés consultar esta comanda, pero tu rol no permite cerrarla."}
                    </Typography>
                    {canManageKitchen ? (
                      <Button
                        variant="contained"
                        disabled={updating}
                        onClick={closeKitchenOrder}
                        sx={{ mt: 1.5, bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}
                      >
                        {updating ? "Cerrando..." : "Cerrar comanda"}
                      </Button>
                    ) : null}
                  </CardContent>
                </Card>
              ) : null}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedOrder(null)} sx={{ textTransform: "none", fontWeight: 900 }}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
